// Try to use Google Places API types as much as possible. We perform
// some data transformation in the job that prefetches this data to
// reduce the amount and cardinality of data we need to consider, so
// make sure this stays in sync with the web API and job systems.

export interface GoogleJwtPayload {
  // Standard  JWT claims
  iss: string;
  sub: string;
  aud: string[] | string;
  exp: number;
  nbf: number;
  iat: number;
  jti: string;
  // Non-standard JWT claims
  azp?: string;
  hd?: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  picture?: string;
  given_name?: string;
  family_name?: string;
  locale?: string;
}
export interface TokenVerificationResponse {
  valid: boolean;
  idinfo?: GoogleJwtPayload;
}

export interface AuthTokenResponse {
  access: string;
  refresh: string;
  valid: boolean;
  idinfo: GoogleJwtPayload;
}

export type NullLatLngLiteral = google.maps.LatLngLiteral | null;

export interface PlannerMapPlace extends CustomPlace {
  description: string;
  time?: string;
  duration?: string;
  sequence?: number;
  position: google.maps.LatLng;
  htmlAttributions?: string[];
  notes?: string;
  pathToNext?: google.maps.LatLngLiteral[];
}

export interface CustomPlace extends google.maps.places.PlaceResult {
  placeId: NonNullable<google.maps.places.PlaceResult["place_id"]>; // Required camelCase version
  photos?: CustomPhoto[];
  phone_number?: string;
  resyId?: number;
}

export interface CustomPhoto extends google.maps.places.PlacePhoto {
  id?: string;
  url: string;
  photoReference: string;
  attribution?: string;
}

export interface Activity extends CustomPlace {
  description?: string;
  notes?: string;
  startTime?: string;
  endTime?: string;
  details?: string;
  type?: string;
  resy?: {
    reservation: ResyReservation;
    venue: ResyVenue;
    slot: ResyVenueTimeSlot;
  }
}

export interface DraftPlan {
  name: string;
  activities: Activity[];
  isPublic: boolean;
}

export interface SavedPlan extends DraftPlan {
  readonly id: string;
  readonly slug: string;
  readonly shareUrl?: string;
  readonly created_at: string;
  readonly updated_at: string;
  rsvpsCount?: number;
}

export type Plan = DraftPlan | SavedPlan;

export const isSavedPlan = (plan: Plan): plan is SavedPlan => 'id' in plan;

export const isDraftPlan = (plan: Plan): plan is DraftPlan => !isSavedPlan(plan);

export interface PathSymbol {
  icon: {
    path: google.maps.SymbolPath;
    scale: number;
    fillColor: string;
    fillOpacity: number;
    strokeColor?: string;
    strokeWeight: number;
  };
  offset: string;
  repeat?: string;
}

export enum SubscriptionTypeChoices {
  FREE,
  TRIAL,
  PRO,
  ULTRA,
}

export enum ConnectionState {
  Connected = 'connected',       // User has a valid token and it's active
  Disabled = 'disabled',         // User has a valid token but it's inactive
  Disconnected = 'disconnected', // User has neither a valid token nor an active connection
  Unknown = 'unknown',           // Connection state cannot be determined
}

export interface Subscription {
  user: number;
  type: SubscriptionTypeChoices;
  description: string;
  stripeSubscriptionId: string | null;
  isActive: boolean;
  features: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ResyVenueTimeSlot {
  id?: number;
  date: { start: string; end: string; };
  size: { max: number; min: number };
  config: { id: number; token: string };
}

export interface ResyReservation {
  resyToken: string;
  reservationId: number;
  venueOptIn: boolean;
}

export interface ResyVenue {
  venue: {
    id: {
      resy: number;
    }
  }
  slots: ResyVenueTimeSlot[];
}

export interface ResyDay {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
}

export interface Person {
  fullName?: string;
  email?: string;
  phone?: string;
  hasCompletedQuiz?: boolean;
  quizCompletedAt?: string;
  preferences?: UserPreferences;
}

export interface UserPreferences {
  favoriteArchetypes: string[];
  preferredVibes: string[];
  priceRangePreference: string;
  cuisinePreferences: string[];
  neighborhoodPreferences: string[];
  averageRating?: number;
}

// Helper functions for coordinate extraction
export const getLatLng = (place: CustomPlace | PlannerMapPlace): google.maps.LatLngLiteral => {
  // For PlannerMapPlace, use the position property
  if ('position' in place && place.position) {
    // google.maps.LatLng object
    if (typeof place.position.lat === 'function' && typeof place.position.lng === 'function') {
      return {
        lat: place.position.lat(),
        lng: place.position.lng()
      };
    }
    // Plain object
    return {
      lat: (place.position as any).lat,
      lng: (place.position as any).lng
    };
  }

  // For CustomPlace, use geometry.location
  if (place.geometry?.location) {
    const loc = place.geometry.location;
    if (typeof loc.lat === 'function' && typeof loc.lng === 'function') {
      return {
        lat: loc.lat(),
        lng: loc.lng()
      };
    }
    // Plain object
    return {
      lat: (loc as any).lat,
      lng: (loc as any).lng
    };
  }

  return {
    lat: (place as any).lat || 0,
    lng: (place as any).lng || 0
  };
}

export const getLat = (place: CustomPlace | PlannerMapPlace): number => {
  if ('position' in place && place.position) {
    if (typeof place.position.lat === 'function') {
      return place.position.lat();
    }
    // Plain object
    return (place.position as any).lat;
  }
  if (place.geometry?.location) {
    const loc = place.geometry.location;
    if (typeof loc.lat === 'function') {
      return loc.lat();
    }
    return (loc as any).lat;
  }
  return 0;
};

export const getLng = (place: CustomPlace | PlannerMapPlace): number => {
  if ('position' in place && place.position) {
    if (typeof place.position.lng === 'function') {
      return place.position.lng();
    }
    // Plain object
    return (place.position as any).lng;
  }
  if (place.geometry?.location) {
    const loc = place.geometry.location;
    if (typeof loc.lng === 'function') {
      return loc.lng();
    }
    return (loc as any).lng;
  }
  return 0;
};

export interface PlannerLineData {
  id: string;
  name: string;
  start_location_id: string;
  end_location_id: string;
  start_coords?: google.maps.LatLngLiteral;
  end_coords?: google.maps.LatLngLiteral;
  transport?: string;
  travelTime?: string;
  // Added for direct coordinate matching if IDs fail (from PlannerTimeline logic)
  start?: { lat: number, lng: number };
  end?: { lat: number, lng: number };
}

// Quiz-related interfaces
export interface QuizVenue {
  id: string;
  name: string;
  archetype: string;
  vibe: string[];
  priceRange: string;
  neighborhood: string;
  imageUrl: string;
  description: string;
}

export enum QuizResponseType {
  LIKE = 'like',
  DISLIKE = 'dislike',
  SKIP = 'skip'
}

export interface QuizProgress {
  totalItems: number;
  answeredCount: number;
  likesCount: number;
  dislikesCount: number;
  skipsCount: number;
  progressPercentage: number;
  isComplete: boolean;
}

export interface QuizResponse {
  id: number;
  venueName: string;
  venueArchetype: string;
  response: QuizResponseType;
  createdAt: string;
}

// Animation direction for swipe gestures
export enum SwipeDirection {
  LEFT = 'left',
  RIGHT = 'right',
  UP = 'up',
  DOWN = 'down'
}

export interface PersonData {
  id: number;
  email: string;
  phone: string;
  avatarUrl: string;
  displayName: string;
  location: string;
}

export interface UserProfile {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  dateJoined: string;
  person: PersonData;
  preferences: UserPreferences | null;
  subscriptions: Subscription[];
}
